import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Edit, Trash2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

export default function IDTypeManagement() {
  const [idTypes, setIdTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ name: '' });

  useEffect(() => {
    loadIdTypes();
  }, []);

  const loadIdTypes = async () => {
    try {
      const response = await api.get('/id-types');
      setIdTypes(response.data);
    } catch (error) {
      toast.error('Failed to load ID types');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await api.put(`/id-types/${editingType.id}`, formData);
        toast.success('ID Type updated');
      } else {
        await api.post('/id-types', formData);
        toast.success('ID Type created');
      }
      setDialogOpen(false);
      setEditingType(null);
      setFormData({ name: '' });
      loadIdTypes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (idType) => {
    setEditingType(idType);
    setFormData({ name: idType.name });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this ID type?')) return;
    try {
      await api.delete(`/id-types/${id}`);
      toast.success('ID Type deleted');
      loadIdTypes();
    } catch (error) {
      toast.error('Failed to delete ID type');
    }
  };

  const handleToggleActive = async (idType) => {
    try {
      await api.put(`/id-types/${idType.id}`, { is_active: !idType.is_active });
      toast.success(`ID Type ${idType.is_active ? 'deactivated' : 'activated'}`);
      loadIdTypes();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openNewDialog = () => {
    setEditingType(null);
    setFormData({ name: '' });
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="id-types-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>ID Types</h1>
          <p className="text-gray-500 text-sm mt-1">Manage guest identification document types</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewDialog} className="bg-gradient-orange hover:opacity-90 text-white btn-glow" data-testid="add-idtype-btn">
              <Plus className="h-4 w-4 mr-2" /> Add ID Type
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader>
              <DialogTitle>{editingType ? 'Edit ID Type' : 'Add New ID Type'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>ID Type Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Passport, Aadhar Card, Driving License"
                  className="border-orange-200 focus:border-orange-400"
                  data-testid="idtype-name-input"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" className="bg-gradient-orange hover:opacity-90 text-white" data-testid="save-idtype-btn">
                  {editingType ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-orange-100 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-gray-800">
            <CreditCard className="h-5 w-5 text-orange-500" />
            All ID Types
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : idTypes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No ID types found. Add common types like Passport, Aadhar Card, Driving License, etc.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-orange-100">
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {idTypes.map((idType) => (
                  <TableRow key={idType.id} className="border-orange-100 table-row-hover">
                    <TableCell className="font-medium text-gray-800">{idType.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={idType.is_active}
                          onCheckedChange={() => handleToggleActive(idType)}
                          data-testid={`toggle-${idType.id}`}
                        />
                        <span className={idType.is_active ? 'text-emerald-600 text-sm' : 'text-gray-400 text-sm'}>
                          {idType.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(idType)} className="text-gray-500 hover:text-orange-600">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(idType.id)} className="text-gray-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
